import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

export const DeleteUserDialog = ({ user, onDeleted }) => {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [purge, setPurge] = useState(true);
  const [deleting, setDeleting] = useState(false);

  const reset = (o) => {
    setOpen(o);
    if (!o) {
      setConfirmText("");
      setPurge(true);
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      const { data } = await api.delete(`/admin/users/${user.id}`, { params: { purge_data: purge } });
      toast.success(`Deleted ${data.deleted_user}${data.purged ? ` and ${data.deleted_incidents} report(s)` : " (reports kept as unassigned)"}`);
      setOpen(false);
      onDeleted(user.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <button onClick={() => reset(true)} data-testid={`delete-user-btn-${user.id}`}
        title="Delete user"
        className="w-8 h-8 flex items-center justify-center border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-sm transition-colors duration-200">
        <Trash2 className="w-3.5 h-3.5" />
      </button>
      <Dialog open={open} onOpenChange={reset}>
        <DialogContent className="bg-[#0A0A0A] border-border rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-[#FF3B30]" /> Delete this profile?
            </DialogTitle>
            <DialogDescription>
              This permanently removes <span className="font-mono text-white">{user.email}</span>. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={purge} onCheckedChange={(v) => setPurge(Boolean(v))}
                data-testid="delete-purge-checkbox" className="mt-0.5" />
              <span className="text-sm text-muted-foreground">
                Also delete all their incident reports and transactions.
                <span className="block text-xs mt-1">If unchecked, their reports stay in the database as unassigned.</span>
              </span>
            </label>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">
                Type the email to confirm
              </p>
              <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                placeholder={user.email} data-testid="delete-confirm-input"
                className="bg-background border-border rounded-sm font-mono text-sm focus-visible:ring-[#FF3B30]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => reset(false)} className="rounded-sm border-border">Cancel</Button>
            <Button onClick={doDelete} disabled={deleting || confirmText.trim().toLowerCase() !== user.email.toLowerCase()}
              data-testid="confirm-delete-user-btn"
              className="rounded-sm bg-[#FF3B30] hover:bg-[#d62f26] text-white gap-2">
              <Trash2 className="w-4 h-4" /> {deleting ? "Deleting…" : "Delete profile"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
